import app from 'flarum/forum/app';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import LogInModal from 'flarum/forum/components/LogInModal';
import Button from 'flarum/common/components/Button';
import Placeholder from 'flarum/common/components/Placeholder';

const NS = 'kellari-guest-notice.forum.';
const CONTACT_EMAIL = 'kellari@busydays.fi';

/**
 * Flarum 1.8 `forum` compat does not expose `flarum/common/override`; mirror core's `override()` behavior.
 * @param {object} object
 * @param {string} method
 * @param {function} newMethod  (originalBound, ...originalArgs) => returnValue
 */
function overrideMethod(object, method, newMethod) {
  const original = object[method];
  object[method] = function (...args) {
    return newMethod.apply(this, [original.bind(this), ...args]);
  };
  Object.assign(object[method], original);
}

/**
 * Flarum exposes patched Mithril as global `m` (core `expose-loader`). ItemList / redraw expect real vnodes,
 * not plain `{ tag, attrs, children }` objects (those break `m.redraw` with undefined `.view`).
 */
function mithrilEl(tag, attrs, children) {
  const m = typeof window !== 'undefined' && window.m;
  if (!m) {
    const ch = children == null ? [] : Array.isArray(children) ? children : [children];
    return { tag, attrs: attrs || {}, children: ch };
  }
  return m(tag, attrs, children);
}

/**
 * Core's `DiscussionListState` has no error flag — a failed `loadPage()` still leaves the list
 * "empty" (`isEmpty()` can't tell a genuinely empty result apart from a broken request). Tag the
 * instance, the first time we see it, so the guest notice doesn't claim discussions are "hidden"
 * when the real cause is a failed request.
 */
function trackLoadFailure(state) {
  if (!state || state.__kellariLoadTracked || typeof state.loadPage !== 'function') {
    return;
  }
  state.__kellariLoadTracked = true;

  overrideMethod(state, 'loadPage', function (original, ...args) {
    return original(...args).then(
      (result) => {
        state.__kellariLoadFailed = false;
        return result;
      },
      (error) => {
        state.__kellariLoadFailed = true;
        throw error;
      }
    );
  });
}

app.initializers.add('kellari-guest-notice', () => {
  if (!DiscussionList) {
    console.error('[kellari-guest-notice] DiscussionList component missing from flarum.core.compat');
    return;
  }

  overrideMethod(DiscussionList.prototype, 'view', function (original) {
    const vnode = original();

    try {
      const state = this.attrs.state;

      if (app.session.user || !state || state.__kellariLoadFailed) {
        return vnode;
      }

      trackLoadFailure(state);

      // Only relabel core's own empty-state `Placeholder`, wherever it ends up in the tree —
      // never replace `vnode` outright, so any future change to `DiscussionList`'s surrounding
      // markup (loading state, wrapper classes, ARIA regions) still applies to guests too.
      const children = Array.isArray(vnode.children) ? vnode.children : [vnode.children].filter(Boolean);
      const placeholderVnode = children.find((child) => child && child.tag === Placeholder);

      if (!placeholderVnode || !placeholderVnode.attrs) {
        return vnode;
      }

      placeholderVnode.attrs.text = [
        app.translator.trans(NS + 'empty_text_guest'),
        mithrilEl('br'),
        mithrilEl('br'),
        mithrilEl(
          Button,
          {
            className: 'Button Button--primary kellari-guest-notice-login',
            onclick: () => app.modal.show(LogInModal),
          },
          app.translator.trans(NS + 'login_button')
        ),
        mithrilEl('br'),
        mithrilEl('br'),
        mithrilEl('span', { className: 'kellari-guest-notice-contact' }, [
          app.translator.trans(NS + 'contact_text') + ' ',
          mithrilEl('a', { href: `mailto:${CONTACT_EMAIL}` }, CONTACT_EMAIL),
        ]),
      ];

      return vnode;
    } catch (error) {
      console.error('[kellari-guest-notice] Falling back to default rendering:', error);
      return vnode;
    }
  });
});
